
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import SearchBar from "@/components/shop/SearchBar";
import VendorList from "@/components/vendors/VendorList";
import VendorFilters from "@/components/vendors/VendorFilters";
import { mockVendors } from "@/utils/mockProducts";
import type { Vendor } from "@/types/shop";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";

const Vendors = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [satisfactionFilter, setSatisfactionFilter] = useState(0);
  const [fulfillmentFilter, setFulfillmentFilter] = useState(0);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 10000]);
  const [categories, setCategories] = useState<string[]>([]);
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const { data: vendors, isLoading, error } = useQuery({
    queryKey: ['verified-vendors', categoryFilter, satisfactionFilter, fulfillmentFilter, priceRange],
    queryFn: async () => {
      console.log("Fetching verified vendors...");
      try {
        let query = supabase
          .from('vendors')
          .select('*')
          .eq('status', 'active')
          .eq('is_verified', true);
        
        if (categoryFilter !== 'all') {
          query = query.eq('business_type', categoryFilter);
        }
        
        const { data, error } = await query.returns<Vendor[]>();

        if (error) throw error;
        
        // Use mock data for development
        return mockVendors;
      } catch (error) {
        console.error("Error fetching vendors:", error);
        toast({
          title: "Error fetching vendors",
          description: "Using mock data instead",
          variant: "destructive",
        });
        return mockVendors;
      }
    },
  });

  useEffect(() => {
    if (vendors) {
      const uniqueCategories = [...new Set(vendors.map(v => v.business_type).filter(Boolean))];
      setCategories(uniqueCategories as string[]);
    }
  }, [vendors]);

  const filteredVendors = vendors?.filter(vendor => {
    const matchesSearch = vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (vendor.business_type && vendor.business_type.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const satisfactionScore = vendor.satisfaction_score || 
      (Math.floor(Math.random() * 10) + 40) / 10;
    const matchesSatisfaction = satisfactionScore >= satisfactionFilter;
    
    const fulfillmentRate = vendor.fulfillment_rate || 
      Math.floor(Math.random() * 20) + 80;
    const matchesFulfillment = fulfillmentRate >= fulfillmentFilter;
    
    const matchesPriceRange = true;
    
    return matchesSearch && matchesSatisfaction && matchesFulfillment && matchesPriceRange;
  });

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 text-center" role="alert">
        <h2 className="text-xl font-bold mb-4">Error loading vendors</h2>
        <p className="text-muted-foreground mb-6">Please try again later</p>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
        <div className="flex items-center gap-4 w-full md:w-auto">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            className="shrink-0"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-3xl font-bold">Verified Vendors</h1>
        </div>
        <div className="w-full md:w-auto">
          <SearchBar 
            searchTerm={searchTerm}
            viewMode={viewMode}
            onSearchChange={setSearchTerm}
            onViewModeChange={setViewMode}
          />
        </div>
      </div>

      {isMobile && (
        <Button 
          variant="outline" 
          className="w-full mb-4"
          onClick={() => setIsFilterVisible(!isFilterVisible)}
        >
          {isFilterVisible ? "Hide Filters" : "Show Filters"}
        </Button>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className={`lg:col-span-1 ${isMobile && !isFilterVisible ? 'hidden' : ''}`}>
          <VendorFilters 
            categories={categories}
            onCategoryChange={setCategoryFilter}
            onSatisfactionChange={setSatisfactionFilter}
            onFulfillmentChange={setFulfillmentFilter}
            onPriceRangeChange={setPriceRange}
          />
        </div>
        <div className="lg:col-span-3">
          <VendorList 
            vendors={filteredVendors} 
            isLoading={isLoading} 
            viewMode={viewMode}
          />
        </div>
      </div>
    </div>
  );
};

export default Vendors;
